// Environment Configuration
export type Environment = 'development' | 'staging' | 'production';

interface EnvironmentConfig {
  env: Environment;
  apiUrl: string;
  sentryDsn?: string;
  gaTrackingId?: string;
  mixpanelToken?: string;
  enableAnalytics: boolean;
  enableErrorTracking: boolean;
  enablePerformanceMonitoring: boolean;
  logLevel: 'debug' | 'info' | 'warn' | 'error';
}

// Detect environment
const getEnvironment = (): Environment => {
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  
  if (hostname.includes('staging') || hostname.includes('lovable.app')) {
    return 'staging';
  }
  
  return 'production';
};

// Environment-specific configurations
const configs: Record<Environment, EnvironmentConfig> = {
  development: {
    env: 'development',
    apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
    enableAnalytics: false,
    enableErrorTracking: false,
    enablePerformanceMonitoring: false,
    logLevel: 'debug',
  },
  staging: {
    env: 'staging',
    apiUrl: import.meta.env.VITE_API_URL || 'https://api-staging.example.com',
    sentryDsn: import.meta.env.VITE_SENTRY_DSN,
    gaTrackingId: import.meta.env.VITE_GA_TRACKING_ID,
    mixpanelToken: import.meta.env.VITE_MIXPANEL_TOKEN,
    enableAnalytics: true,
    enableErrorTracking: true,
    enablePerformanceMonitoring: false,
    logLevel: 'info',
  },
  production: {
    env: 'production',
    apiUrl: import.meta.env.VITE_API_URL || 'https://api.example.com',
    sentryDsn: import.meta.env.VITE_SENTRY_DSN,
    gaTrackingId: import.meta.env.VITE_GA_TRACKING_ID,
    mixpanelToken: import.meta.env.VITE_MIXPANEL_TOKEN,
    enableAnalytics: true,
    enableErrorTracking: true,
    enablePerformanceMonitoring: false,
    logLevel: 'error',
  },
};

// Get current configuration
export const config = configs[getEnvironment()];

// Feature flags
export const featureFlags = {
  enableVoiceCommands: import.meta.env.VITE_ENABLE_VOICE === 'true',
  enableSatelliteData: import.meta.env.VITE_ENABLE_SATELLITE === 'true',
  enableUSSDSimulator: import.meta.env.VITE_ENABLE_USSD === 'true',
  enableOfflineMode: true,
  enablePWA: true,
};

// Helper to check if feature is enabled
export const isFeatureEnabled = (feature: keyof typeof featureFlags): boolean => {
  return featureFlags[feature];
};

// Logging utility
export const logger = {
  debug: (...args: any[]) => {
    if (config.logLevel === 'debug') {
      console.log('[DEBUG]', ...args);
    }
  },
  info: (...args: any[]) => {
    if (['debug', 'info'].includes(config.logLevel)) {
      console.info('[INFO]', ...args);
    }
  },
  warn: (...args: any[]) => {
    if (['debug', 'info', 'warn'].includes(config.logLevel)) {
      console.warn('[WARN]', ...args);
    }
  },
  error: (...args: any[]) => {
    console.error('[ERROR]', ...args);
  },
};
